import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";

//Filters
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import {
  getExchangeRateList,
  getCurrencyList,
  editCurrency,
  deleteExchangeRate,
  addCurrency,
  addExchangeRate,
  downloadExchangeRateCount,
  deleteCurrency,
} from "@/requests/exchangeRate";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { DataOptions } from "vuetify";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import { CurrencyItems, CurrencyItemsResponse } from "@/models/api/administration/exchangeRate/CurrencyItems";
import {
  ExchangeRateItemsResponse,
  ExchangeRateItems,
  ExchangeRateItemsAdd,
} from "@/models/api/administration/exchangeRate/ExchangeRateItems";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";

export interface AdministrationExchangeRateStateInterface {
  //Table 1
  currencyFilters: null | Array<CriterionBase>;
  currencyDataTableOptions: DataOptions;
  currencyList: Array<CurrencyItems>;
  currencyCountItems: number;
  currencyId: number;
  currencyItem: CurrencyItems;
  currencyItemToEdit: CurrencyItems;
  currencySelected: boolean;
  canDeleteCurrency: boolean;

  //Table 2
  exchangeRateFilters: null | Array<CriterionBase>;
  exchangeRateDataTableOptions: DataOptions;
  exchangeRateList: Array<ExchangeRateItems>;
  exchangeRateCountItems: number;
  exchangeRateIdToDelete: number;
  exchangeRateToAdd: ExchangeRateItemsAdd;

  //Dialogs
  //Currency
  isEditDialog: boolean;
  isNew: boolean;
  isReadOnly: boolean;

  //ExchangeRate
  isAddExchangeRateDialog: boolean;
}

export const AdministrationExchangeRateState: AdministrationExchangeRateStateInterface = {
  currencyFilters: null,
  currencyDataTableOptions: new DefaultDataOptions(),
  currencyList: [],
  currencyCountItems: -1,
  currencyId: 0,
  currencyItem: new CurrencyItems(),
  currencyItemToEdit: new CurrencyItems(),
  currencySelected: false,
  canDeleteCurrency: false,

  exchangeRateFilters: null,
  exchangeRateDataTableOptions: new DefaultDataOptions(),
  exchangeRateList: [],
  exchangeRateCountItems: -1,
  exchangeRateIdToDelete: -1,
  exchangeRateToAdd: new ExchangeRateItemsAdd(),

  isEditDialog: false,
  isNew: false,
  isReadOnly: true,

  isAddExchangeRateDialog: false,
};

const AdministrationExchangeRateModule = defineModule({
  state: AdministrationExchangeRateState as AdministrationExchangeRateStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    //Table 1
    setCurrencyStandardFilters(state, param: Array<CriterionBase>) {
      state.currencyFilters = param;
    },
    setCurrencyList(state, param: CurrencyItemsResponse) {
      state.currencyList = param.items;
      state.currencyCountItems = param.totalCount;
    },
    setCurrencyDataTableOptions(state, param: DataOptions) {
      state.currencyDataTableOptions = param;
    },
    setSelectedItemId(state, param: number) {
      state.currencyId = param;
      state.currencySelected = true;
    },
    setSelectedItem(state, param: CurrencyItems) {
      state.currencyItem = param;
      state.currencyItemToEdit = new CurrencyItems(param.id, param.name, param.code, param.autoUpdate);
    },
    setCanDeleteCurrency(state, param: boolean) {
      state.canDeleteCurrency = param;
    },

    //Table 2
    setExchangeRateAdditionalFilters(state, param: Array<CriterionBase>) {
      state.exchangeRateFilters = param;
    },
    setExchangeRateList(state, param: ExchangeRateItemsResponse) {
      state.exchangeRateList = param.items;
      state.exchangeRateCountItems = param.totalCount;
    },
    setExchangeRateDataTableOptions(state, param: DataOptions) {
      state.exchangeRateDataTableOptions = param;
    },
    setExchangeRateToDelete(state, param: number) {
      state.exchangeRateIdToDelete = param;
    },
    setExchangeRateToAdd(state) {
      const currencyCode = state.currencyItem.code;
      state.exchangeRateToAdd = new ExchangeRateItemsAdd(currencyCode);
    },

    //Dialogs
    //Currency
    setEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setIsNew(state, param: boolean) {
      state.isNew = param;
      state.isEditDialog = param;
      if (param) state.currencyItemToEdit = new CurrencyItems();
    },

    //ExchangeRate
    setExchangeRateDialog(state, param: boolean) {
      state.isAddExchangeRateDialog = param;
    },
  },

  actions: {
    async downloadCurrencies(context) {
      const { commit, state } = _moduleActionContext(context);
      const currencies = await getCurrencyList({
        count: state.currencyDataTableOptions.itemsPerPage,
        page: state.currencyDataTableOptions.page - 1,
        criterions: _.cloneDeep(state.currencyFilters),
        filterName: FILTER_NAMES.ExchangeRateStandard,
      });
      commit.setCurrencyList(currencies);
    },
    async downloadExchangeRates(context) {
      const { commit, state } = _moduleActionContext(context);
      const exchangeRate = await getExchangeRateList(
        {
          count: state.exchangeRateDataTableOptions.itemsPerPage,
          page: state.exchangeRateDataTableOptions.page - 1,
          criterions: _.cloneDeep(state.exchangeRateFilters),
          filterName: FILTER_NAMES.ExchangeRateAdditional,
        },
        state.currencyId
      );
      commit.setExchangeRateList(exchangeRate);
    },
    async editCurrency(context, model: CurrencyItems) {
      const { dispatch, rootDispatch, commit } = _moduleActionContext(context);
      const response = await editCurrency(model);
      if (response == null) commit.setEditDialog(false);
      await dispatch.downloadCurrencies();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async deleteExchangeRate(context) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const id = state.exchangeRateIdToDelete;
      await deleteExchangeRate(id);
      await dispatch.downloadExchangeRates();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async addNewCurrency(context, model: CurrencyItems) {
      const { dispatch, rootDispatch, commit } = _moduleActionContext(context);
      const response = await addCurrency(model);
      if (response == null) commit.setEditDialog(false);
      await dispatch.downloadCurrencies();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async addNewExchangeRate(context, model: ExchangeRateItemsAdd) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      const currencyId = state.currencyId;
      //Replace string to number
      model.exchangeRateValue = Number(model.exchangeRateValue.toString().replace(",", "."));
      await addExchangeRate(model, currencyId);
      await dispatch.downloadExchangeRates();
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async downloadExchangeRateCount(context, currencyId: number) {
      const { commit } = _moduleActionContext(context);
      //const currencyId = state.currencyId;
      const counter = await downloadExchangeRateCount(currencyId);
      if (counter === 0) commit.setCanDeleteCurrency(true);
      else commit.setCanDeleteCurrency(false);
    },
    async deleteCurrency(context, model: CurrencyItems) {
      const { rootDispatch, dispatch, commit } = _moduleActionContext(context);
      const isRestartTaskConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(i18n.t("doYouReallyWantToDeleteCurrency")) + "?\n" + model.code,
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "550px"
        )
      )) as boolean;
      if (isRestartTaskConfirmed) {
        await deleteCurrency(model.id);
        commit.setEditDialog(false);
        commit.setCanDeleteCurrency(false);
        await dispatch.downloadCurrencies();
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
  },
});

export default AdministrationExchangeRateModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationExchangeRateModule);
